import type { UserEditableFormControlElement, SetupFn, ValidatorFn } from "../utils/types";
import { VisibleFieldMap } from "./visibleFieldMap";
import { FieldValidation } from "./fieldValidation";

export class UnobtrusiveValidator {

    private initializedForms = new WeakSet<HTMLFormElement>();

    private customSetupHandlers: Record<string, SetupFn> = {};
    private defaultFieldHandlers = new WeakMap<UserEditableFormControlElement, EventListener>();

    private customValidators: Record<string, ValidatorFn> = {};
    private visibleFieldMap = new VisibleFieldMap();
    private fieldValidation = new FieldValidation(this.customValidators, this.visibleFieldMap);

    constructor() {
        // Initial automatic setup for existing forms
        document.querySelectorAll("form").forEach((form) => {
            this.setupValidation(form);
        });

        // MutationObserver to watch for new forms/elements
        new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                mutation.addedNodes.forEach((node) => {
                    if (!(node instanceof HTMLElement)) return;

                    if (node.matches("form")) {
                        this.setupValidation(node);

                        for (const selector in this.customSetupHandlers) {
                            this.executeRegisteredSetup(selector, node);
                        }
                    } else {
                        node.querySelectorAll("form").forEach((form) => {
                            this.setupValidation(form);

                            for (const selector in this.customSetupHandlers) {
                                this.executeRegisteredSetup(selector, form);
                            }
                        });
                    }
                });
            }
        }).observe(document.body, { childList: true, subtree: true });
    }


    // === Public API registrations ===

    /**
     * Register a setup handler for a selector.
     * runtime-checked: setupFn may be unknown (for JS consumers)
     */
    registerSetup(selector: string, setupFn: unknown): void {
        if (typeof setupFn !== "function") {
            console.warn(`[UnobtrusiveValidator] Setup for selector "${selector}" must be a function.`);
            return;
        }

        if (this.customSetupHandlers[selector]) {
            console.warn(`[UnobtrusiveValidator] A setup handler is already registered for selector "${selector}".`);
            return;
        }

        this.customSetupHandlers[selector] = setupFn as SetupFn;

        document.querySelectorAll("form").forEach((form) => {
            this.executeRegisteredSetup(selector, form);
        });
    }

    /**
     * Register a custom validator by rule name.
     */
    registerValidator(ruleName: string, validatorFn: unknown): void {
        if (typeof validatorFn !== "function") {
            console.warn(`[UnobtrusiveValidator] Validator for rule "${ruleName}" must be a function.`);
            return;
        }

        if (this.customValidators[ruleName]) {
            console.warn(`[UnobtrusiveValidator] A validator for "${ruleName}" is already registered.`);
            return;
        }

        this.customValidators[ruleName] = validatorFn as ValidatorFn;
    }

    /**
     * Returns true if valid, false if invalid.
     * Also sets up error feedback in the DOM (data-valmsg-for).
     */
    validateField(field: Element, form: HTMLFormElement): boolean {
        if (!this.isUserEditableFormControl(field)) {
            console.warn(`[UnobtrusiveValidator] Element "${field}" must be a user editable form control element (input, textArea or select).`);
            return true;
        }

        return this.fieldValidation.validateField(field, form);
    }

    // === Form-wide validation ===
    validateForm(form: HTMLFormElement): boolean {
        let valid = true;

        form.querySelectorAll<UserEditableFormControlElement>(`input[data-val="true"],select[data-val="true"],textArea[data-val="true"]`).forEach((field) => {
            const ok = this.validateField(field, form);
            if (!ok) valid = false;
        });

        return valid;
    }


    // === Setup function to attach handlers to a given form ===
    private setupValidation(form: HTMLFormElement): void {
        if (this.initializedForms.has(form)) return;
        this.initializedForms.add(form);

        form.querySelectorAll<UserEditableFormControlElement>(`input[data-val="true"],select[data-val="true"],textArea[data-val="true"]`).forEach((field) => {
            let handler: EventListener = () => {
                this.validateField(field, form);
            };

            if (field instanceof HTMLSelectElement) {
                field.addEventListener('change', handler);
            } else {
                field.addEventListener('blur', handler);
            }

            this.defaultFieldHandlers.set(field, handler);
        });

        form.addEventListener('submit', (e) => {
            if (!this.validateForm(form)) {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        });

        form.addEventListener('reset', () => {
            this.fieldValidation.clearFormValidation(form);
        });
    }

    // === Custom setup execution helper ===
    private executeRegisteredSetup(selector: string, form: HTMLFormElement): void {
        const setupFn = this.customSetupHandlers[selector];
        if (!setupFn) return;

        form.querySelectorAll<UserEditableFormControlElement>(`input${selector}[data-val="true"],select${selector}[data-val="true"],textArea${selector}[data-val="true"]`).forEach((field) => {
            this.removeDefaultHandlers(field);

            const visibleField = setupFn(field, form);

            if (visibleField) {
                this.visibleFieldMap.set(field, visibleField);
            }
        });
    }

    // remove default handlers (used when a custom setup takes over)
    private removeDefaultHandlers(field: UserEditableFormControlElement): void {
        const handler = this.defaultFieldHandlers.get(field);
        if (!handler) return;

        field.removeEventListener('blur', handler);
        field.removeEventListener('change', handler);
        this.defaultFieldHandlers.delete(field);
    }

    // user editable form control type guard
    private isUserEditableFormControl(el: Element): el is UserEditableFormControlElement {
        return el instanceof HTMLInputElement ||
            el instanceof HTMLTextAreaElement ||
            el instanceof HTMLSelectElement;
    }
}
